// src/components/dashboard/trainer/TrainerTrainingsTable.js
import React from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardCard from '../DashboardCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_VARIANT = {
  'Upcoming': 'info',
  'Attendance Pending': 'danger',
  'Completed': 'success',
  'Canceled': 'secondary',
  'Postponed': 'warning',
};

function TrainerTrainingsTable({ trainings, loading, onStatusChanged }) {
  const handleStatusChange = async (trainingId, newStatus) => {
    const { value: reason } = await Swal.fire({
      title: newStatus === 'Canceled' ? 'Cancel this training?' : 'Postpone this training?',
      input: 'textarea',
      inputLabel: 'Reason (required)',
      inputPlaceholder: 'Type a reason...',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: newStatus === 'Canceled' ? 'Yes, cancel' : 'Yes, postpone',
      inputValidator: (value) => !value?.trim() && 'A reason is required.',
    });
    if (!reason) return;

    try {
      const response = await fetch(`${API_BASE_URL}/trainings/update_training_status.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainingId, status: newStatus, reason }),
      });
      const data = await response.json();
      if (data.success) {
        Swal.fire({ icon: 'success', text: `Training marked as ${newStatus}.` });
        onStatusChanged?.();
      } else {
        Swal.fire({ icon: 'error', text: data.message || 'Failed to update training status.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', text: 'Error: ' + error.message });
    }
  };

  return (
    <DashboardCard
      title="My Trainings"
      icon="bi-journal-check"
      loading={loading}
      isEmpty={!loading && trainings.length === 0}
      emptyMessage="No trainings assigned yet."
    >
      <Table striped bordered hover responsive size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Code</th>
            <th>Title</th>
            <th>Date</th>
            <th>Location</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {trainings.map(t => (
            <tr key={t.id}>
              <td>{t.code}</td>
              <td>{t.title}</td>
              <td>{t.start_date}{t.end_date !== t.start_date ? ` – ${t.end_date}` : ''}</td>
              <td>{t.location}</td>
              <td><Badge bg={STATUS_VARIANT[t.status] || 'secondary'}>{t.status}</Badge></td>
              <td>
                {(t.status === 'Attendance Pending' || t.status === 'Completed') && (
                  <Link to={`/training-provider/attendance/${t.id}`}>
                    <Button size="sm" variant="primary" className="me-2">Mark Attendance</Button>
                  </Link>
                )}
                {t.status === 'Completed' && (
                  // Upload is already allowed backend-side for a completed
                  // training (no status check there) — this table just
                  // never linked anywhere a trainer could reach the
                  // Documents/Attachments Upload tab from their own
                  // dashboard for one.
                  <Link to={`/training/view/${t.id}`}>
                    <Button size="sm" variant="secondary">Documents</Button>
                  </Link>
                )}
                {t.status === 'Upcoming' && (
                  <>
                    <Button size="sm" variant="warning" className="me-2" onClick={() => handleStatusChange(t.id, 'Postponed')}>
                      Postpone
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleStatusChange(t.id, 'Canceled')}>
                      Cancel
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </DashboardCard>
  );
}

export default TrainerTrainingsTable;
